// @flow strict

import * as React from 'react';
import classNames from '@ta/common.classnames';
import styles from './Button.css';

type Props = {|
  /** The size of the button. Defaults to medium */
  size?: 'small' | 'medium' | 'large',
  /** The button type. Defaults to primary */
  theme?: 'original' | 'primary' | 'secondary' | 'standard' | 'commerce',
  /** whether the button's width should be the same as the parent's width */
  fullWidth?: boolean,
  /** whether button should appear disabled */
  disabled?: boolean,
  /** The default behavior of the button when submitting. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button   */
  type?: 'submit' | 'reset' | 'button',
  /** The semantic HTML element type of the Button. Defaults to 'button'. This should be the element type for most purposes.
   * This override is for the special case in which it may conflict with other HTML semantics (e.g. a link styled to look like a button; it is not valid to have a button inside of an anchor )
   */
  elementType?: 'button' | 'span',
  /** The callback fired on button click */
  onClick?: (event: SyntheticMouseEvent<HTMLButtonElement>) => mixed,
  /** The descendant React nodes of the Button */
  children: React.Node,
|};

/**
 * Render a TripAdvisor-themed button
 */
const Button = ({
  size = 'medium',
  theme = 'primary',
  fullWidth,
  disabled,
  type = 'button',
  elementType = 'button',
  onClick,
  children,
}: Props) =>
  React.createElement(
    elementType,
    {
      className: classNames(styles.root, {
        [styles.fullWidth]: fullWidth,
        [styles.small]: size === 'small',
        [styles.large]: size === 'large',
        [styles.secondary]: theme === 'secondary',
        [styles.original]: theme === 'original',
        [styles.standard]: theme === 'standard',
        [styles.commerce]: theme === 'commerce',
      }),
      disabled,
      onClick,
      type: elementType === 'button' ? type : undefined,
    },
    children,
  );

export default Button;
